/**
 * Browser auto-translation (Chrome / Google Translate), Grammarly, and similar
 * extensions mutate the text nodes React renders — swapping, wrapping, or
 * removing them out from under the reconciler. On its next commit React calls
 * `parent.removeChild(node)` / `parent.insertBefore(node, ref)` against a node
 * the extension already moved, and the DOM throws a `NotFoundError`:
 *
 *   Failed to execute 'removeChild' on 'Node': The node to be removed is not a
 *   child of this node.
 *   Failed to execute 'insertBefore' on 'Node': The node before which the new
 *   node is to be inserted is not a child of this node.
 *
 * This app has no React error boundary, and React 19's `onUncaughtError` is
 * wired straight to Sentry (`src/main.tsx`), so the throw escapes commit,
 * unmounts the tree (white screen), AND floods Sentry — all from a mutation we
 * never made. Highly likely here: we ship our own i18n (en/ko/zh), so users on
 * other browser locales get Chrome's "translate this page" prompt over our
 * already-live, frequently-updating trade UI.
 *
 * Two layers, both exported:
 *   - `initDomNodeErrorGuard()` — makes `removeChild` / `insertBefore` no-op
 *     instead of throw when the node was already detached. React re-appends on
 *     the next render, so the hard crash degrades to an invisible transient.
 *     Must run before the first React commit (called in `src/main.tsx`).
 *   - `isDomNodeError()` — predicate for the Sentry `beforeSend` drop
 *     (`integrations/sentry/sentry.ts`), as defense-in-depth for any variant
 *     the guard doesn't intercept (e.g. a different DOM API or surface).
 *
 * Canonical workaround for facebook/react#11538.
 */

function getErrorMessage(error: unknown): string {
	if (typeof error === "string") return error;
	// DOMException is not reliably `instanceof Error` across engines, but it —
	// like Error — carries a string `message`, so match on the shape instead.
	if (error && typeof error === "object" && "message" in error) {
		const message = (error as { message?: unknown }).message;
		if (typeof message === "string") return message;
	}
	return "";
}

// The `NotFoundError` DOMExceptions thrown when React commits against a node a
// translator/extension already moved. The "not a child of this node" tail is
// shared by the removeChild / insertBefore / replaceChild variants, so we
// require the method name too to stay specific and avoid false positives.
const DOM_NODE_ERROR_RE =
	/Failed to execute '(?:removeChild|insertBefore|replaceChild)' on 'Node'.*not a child of this node/i;

export function isDomNodeError(error: unknown): boolean {
	return DOM_NODE_ERROR_RE.test(getErrorMessage(error));
}

let installed = false;

export function initDomNodeErrorGuard(): void {
	if (installed) return;
	if (typeof Node !== "function" || !Node.prototype) return;
	installed = true;

	const originalRemoveChild = Node.prototype.removeChild;
	Node.prototype.removeChild = function removeChild<T extends Node>(
		this: Node,
		child: T,
	): T {
		// Node was detached by an external DOM mutation — removing it would throw.
		if (child.parentNode !== this) return child;
		return originalRemoveChild.call(this, child) as T;
	};

	const originalInsertBefore = Node.prototype.insertBefore;
	Node.prototype.insertBefore = function insertBefore<T extends Node>(
		this: Node,
		newNode: T,
		referenceNode: Node | null,
	): T {
		// Reference node was moved out from under us — inserting would throw.
		if (referenceNode && referenceNode.parentNode !== this) return newNode;
		return originalInsertBefore.call(this, newNode, referenceNode) as T;
	};
}
